/**
 * AI-классификация тикетов клиента (v0.9.65 #9.63).
 * Кэш 24 часа на backend. Lazy-load по кнопке (вызов DeepSeek дорогой).
 */
import { useState } from 'react';
import { Brain, Loader2, RefreshCw, Ticket as TicketIcon } from 'lucide-react';
import { api } from '../api/client';

interface Cat { label: string; count: number; examples: string[] }
interface Data { categories: Cat[]; total_tickets: number; period_days: number; model?: string; reason?: string }

export function TicketCategoriesWidget({ orgId }: { orgId: string }) {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get<Data>(`/api/v1/organizations/${orgId}/ticket-categories`);
      setData(data);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
        <h3 className="font-semibold text-white text-sm inline-flex items-center gap-2">
          <Brain size={14} className="text-purple-400" /> AI-категории тикетов
          {data?.total_tickets ? (
            <span className="text-xs text-slate-500">({data.total_tickets} за {data.period_days} дней)</span>
          ) : null}
        </h3>
        <button onClick={load} disabled={loading}
                title={data ? 'Перечитать (DeepSeek — может занять ~10 сек)' : 'Запустить AI-классификацию (DeepSeek)'}
                className="text-[11px] bg-slate-800 hover:bg-purple-900/50 text-slate-300 hover:text-purple-300 px-2 py-1 rounded inline-flex items-center gap-1 disabled:opacity-50">
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          {data ? 'Обновить' : 'Классифицировать'}
        </button>
      </header>
      <div className="p-3">
        {!data && !loading && !err && (
          <div className="text-xs text-slate-500 text-center py-3">
            Нажмите «Классифицировать» чтобы DeepSeek сгруппировал тикеты этого
            клиента по типу проблемы. Результат кэшируется на 24 часа.
          </div>
        )}
        {err && <div className="text-xs text-rose-300">{err}</div>}
        {data?.reason && <div className="text-xs text-slate-500 py-2">{data.reason}</div>}
        {data?.categories && data.categories.length > 0 && (
          <ul className="space-y-1.5">
            {data.categories.map((c, i) => {
              const pct = data.total_tickets > 0 ? Math.round(c.count * 100 / data.total_tickets) : 0;
              return (
                <li key={i} className="bg-slate-950/40 border border-slate-800 rounded px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-200">{c.label}</span>
                    <span className="text-xs text-slate-400 font-mono">{c.count} · {pct}%</span>
                  </div>
                  <div className="mt-1 h-1 bg-slate-800 rounded overflow-hidden">
                    <div className="h-full bg-purple-500/60" style={{ width: `${pct}%` }} />
                  </div>
                  {c.examples.length > 0 && (
                    <div className="mt-1 text-[10px] text-slate-500 inline-flex items-center gap-1 flex-wrap">
                      <TicketIcon size={9} />
                      {c.examples.join(', ')}
                    </div>
                  )}
                </li>
              );
            })}
            {data.model && (
              <li className="text-[10px] text-slate-600 text-right pr-2 pt-1">
                модель: {data.model}
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
